// ─── Shared circle fragment ───────────────────────────────────────────

const CIRCLE_FIELDS = /* GraphQL */ `
  id name displayTitle description coverUrl category access myStatus
  type
  brandName
  alumniTeamsEnabled
  candidateTeams { circleId name existingMemberCount }
  memberCount createdAt
  problemHook problemStatement
  campaigns { id title description displayOrder }
`;

// ─── Post fragment ───────────────────────────────────────────────────

const POST_FIELDS = /* GraphQL */ `
  id circleId content createdAt updatedAt
  linkUrl linkTitle linkDescription linkImageUrl linkDomain
  author { id name pictureUrl }
  attachments { id originalName url fileSize mimeType }
  comments { id author { id name pictureUrl } content createdAt }
  reactions { id user { id name } reaction }
`;

// ─── HumanCircle fragment ─────────────────────────────────────────────

const HUMAN_CIRCLE_FIELDS = /* GraphQL */ `
  id status confidence joinedAt
  circle { ${CIRCLE_FIELDS} }
`;

// ─── Circle suggestion (home feed) ───────────────────────────────────

export const SUGGEST_CIRCLE_Q = /* GraphQL */ `
  query SuggestCircle {
    suggestCircle {
      ${CIRCLE_FIELDS}
      memberAvatars
      plannedGatherings { title description format cadence }
    }
  }
`;

// ─── Coalition / League suggestions (separate home surfaces) ─────────

export const SUGGEST_COALITION_Q = /* GraphQL */ `
  query SuggestCoalition {
    suggestCoalition {
      ${CIRCLE_FIELDS}
      memberAvatars
    }
  }
`;

export const SUGGEST_LEAGUE_Q = /* GraphQL */ `
  query SuggestLeague {
    suggestLeague {
      ${CIRCLE_FIELDS}
      memberAvatars
    }
  }
`;

// ─── My Circles (confirmed) ───────────────────────────────────────────

export const MY_CIRCLES_QUERY = /* GraphQL */ `
  query MyCircles {
    myCircles { ${HUMAN_CIRCLE_FIELDS} }
  }
`;

// Variant scoped by surface ('circle' | 'coalition' | 'league'). Used by
// the dedicated Coalitions / Leagues list views.
export const MY_CIRCLES_BY_TYPE_QUERY = /* GraphQL */ `
  query MyCirclesByType($type: String!) {
    myCircles(type: $type) { ${HUMAN_CIRCLE_FIELDS} }
  }
`;

// ─── Suggested Circles (inferred) ────────────────────────────────────

export const SUGGESTED_CIRCLES_QUERY = /* GraphQL */ `
  query SuggestedCircles {
    suggestedCircles { ${HUMAN_CIRCLE_FIELDS} }
  }
`;

// ─── Circle browse queries ────────────────────────────────────────────

export const CIRCLES_QUERY = /* GraphQL */ `
  query Circles {
    circles { ${CIRCLE_FIELDS} }
  }
`;

// Browse circles filtered by surface type ('circle' | 'coalition' | 'league'),
// sorted by popularity. Used by the Recommended tabs on the Coalitions
// and Leagues list views.
export const CIRCLES_BY_TYPE_QUERY = /* GraphQL */ `
  query CirclesByType($type: String!, $limit: Int) {
    circles(type: $type, limit: $limit) { ${CIRCLE_FIELDS} }
  }
`;

export const CIRCLE_QUERY = /* GraphQL */ `
  query Circle($id: ID!) {
    circle(id: $id) {
      ${CIRCLE_FIELDS}
      members { id status joinedAt human { id name pictureUrl } }
    }
  }
`;

export const CIRCLE_POSTS_QUERY = /* GraphQL */ `
  query CirclePosts($circleId: ID!) {
    circlePosts(circleId: $circleId) { ${POST_FIELDS} }
  }
`;

// ─── Explore cards ───────────────────────────────────────────────────

export const CIRCLE_EXPLORE_CARDS_QUERY = /* GraphQL */ `
  query CircleExploreCards($circleId: ID!, $count: Int) {
    circleExploreCards(circleId: $circleId, count: $count) {
      id title text
      human { id name pictureUrl }
    }
  }
`;

// ─── Link preview ────────────────────────────────────────────────────

export const PREVIEW_LINK_QUERY = /* GraphQL */ `
  query PreviewLink($url: String!) {
    previewLink(url: $url) { url title description imageUrl domain }
  }
`;

// ─── Join / decline mutations ─────────────────────────────────────────

export const JOIN_CIRCLE_MUTATION = /* GraphQL */ `
  mutation JoinCircle(
    $circleId: ID!
    $teamCircleIds: [ID!]
    $memberRole: String
    $contributionOptions: [String!]
    $contributionNote: String
  ) {
    joinCircle(
      circleId: $circleId
      teamCircleIds: $teamCircleIds
      memberRole: $memberRole
      contributionOptions: $contributionOptions
      contributionNote: $contributionNote
    ) { ${HUMAN_CIRCLE_FIELDS} }
  }
`;

export const DECLINE_CIRCLE_MUTATION = /* GraphQL */ `
  mutation DeclineCircle($circleId: ID!) { declineCircle(circleId: $circleId) }
`;

export const CONFIRM_CIRCLE_MUTATION = /* GraphQL */ `
  mutation ConfirmCircle($circleId: ID!) {
    confirmCircle(circleId: $circleId) { ${HUMAN_CIRCLE_FIELDS} }
  }
`;

// ─── Post mutations ──────────────────────────────────────────────────

export const CREATE_CIRCLE_POST_MUTATION = /* GraphQL */ `
  mutation CreateCirclePost($circleId: ID!, $content: String!, $attachments: [CircleAttachmentInput!]) {
    createCirclePost(circleId: $circleId, content: $content, attachments: $attachments) {
      ${POST_FIELDS}
    }
  }
`;

export const UPLOAD_ATTACHMENT_MUTATION = /* GraphQL */ `
  mutation UploadCircleAttachment($fileName: String!, $mimeType: String!, $data: String!, $fileSize: Int!) {
    uploadCircleAttachment(fileName: $fileName, mimeType: $mimeType, data: $data, fileSize: $fileSize) {
      originalName url fileSize mimeType
    }
  }
`;

export const ADD_COMMENT_MUTATION = /* GraphQL */ `
  mutation AddCirclePostComment($postId: ID!, $content: String!) {
    addCirclePostComment(postId: $postId, content: $content) {
      id author { id name pictureUrl } content createdAt
    }
  }
`;

export const TOGGLE_REACTION_MUTATION = /* GraphQL */ `
  mutation ToggleReaction($postId: ID!, $reaction: String!) {
    toggleCirclePostReaction(postId: $postId, reaction: $reaction) {
      id reactions { id user { id name } reaction }
    }
  }
`;

export const DELETE_POST_MUTATION = /* GraphQL */ `
  mutation DeleteCirclePost($id: ID!) { deleteCirclePost(id: $id) }
`;

export const DELETE_COMMENT_MUTATION = /* GraphQL */ `
  mutation DeleteCirclePostComment($id: ID!) { deleteCirclePostComment(id: $id) }
`;
